import React, { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, GitBranch } from 'lucide-react';
import { careerLog } from '../data/careerLog';
import { CareerCommit, CommitLane } from '../types';

/* ------------------------------------------------------------------------------------------------
 * Graph model: derived entirely from the ordered log, one fixed-height cell per row (like
 * `git log --graph` prints one line per commit). Each cell holds the lane pieces passing through it.
 * ---------------------------------------------------------------------------------------------- */

type SideLane = Exclude<CommitLane, 'main'>;

interface Branch {
  name: string;
  lane: SideLane;
  /** Row of the newest commit on the branch (where its pill sits). */
  tip: number;
  /** Row of the oldest commit; the branch forks off main in the row below it. */
  base: number;
  /** Merges back into main just above `tip`; otherwise it stays open. */
  merged: boolean;
}

/** One piece of lane inside a row cell: above/below the node, or a curve in the cell's top half. */
export interface Segment {
  lane: CommitLane;
  kind: 'up' | 'down' | 'fork' | 'merge';
  dashed: boolean;
}

export interface Ref {
  label: string;
  lane: CommitLane;
  kind: 'head' | 'branch' | 'upcoming';
}

export interface GraphRow {
  segments: Segment[];
  refs: Ref[];
  isHead: boolean;
}

export interface CareerGraphModel {
  rows: GraphRow[];
  branches: Branch[];
  /** Branch HEAD points at, for the header badge. */
  headBranch: string | null;
}

const branchName = (c: CareerCommit) => (c.lane === 'lead' ? 'lead' : c.scope ?? c.hash);

export function buildGraph(log: CareerCommit[]): CareerGraphModel {
  const last = log.length - 1;
  const head = log.findIndex((c) => c.lane === 'work' && c.status === 'current');
  // Main is dashed above "now": above HEAD, or above the newest commit that isn't upcoming.
  const present = head >= 0 ? head : log.findIndex((c) => c.status !== 'upcoming');

  const byName = new Map<string, Branch>();
  log.forEach((c, i) => {
    if (c.lane === 'main') return;
    const name = branchName(c);
    const existing = byName.get(name);
    if (existing) existing.base = i;
    else byName.set(name, { name, lane: c.lane, tip: i, base: i, merged: c.status === 'done' });
  });
  const branches = Array.from(byName.values());

  const rows = log.map((c, i): GraphRow => {
    const segments: Segment[] = [];

    if (i > 0) segments.push({ lane: 'main', kind: 'up', dashed: i <= present });
    if (i < last) segments.push({ lane: 'main', kind: 'down', dashed: i < present });

    for (const b of branches) {
      if (i === b.base + 1) segments.push({ lane: b.lane, kind: 'fork', dashed: false });
      if (i > b.tip && i <= b.base) segments.push({ lane: b.lane, kind: 'up', dashed: false });
      if (i >= b.tip && i <= b.base) segments.push({ lane: b.lane, kind: 'down', dashed: false });
      if (i === b.tip && b.merged) segments.push({ lane: b.lane, kind: 'merge', dashed: false });
    }

    const refs: Ref[] = [];
    if (c.status === 'upcoming') refs.push({ label: c.scope ?? c.hash, lane: c.lane, kind: 'upcoming' });
    const tipOf = branches.find((b) => b.tip === i);
    if (tipOf) {
      refs.push(
        i === head
          ? { label: `HEAD → ${tipOf.name}`, lane: tipOf.lane, kind: 'head' }
          : { label: tipOf.name, lane: tipOf.lane, kind: 'branch' }
      );
    }

    return { segments, refs, isHead: i === head };
  });

  return { rows, branches, headBranch: head >= 0 ? branchName(log[head]) : null };
}

/** Short name in the log pane: the scope, else the tag, else the hash. */
export const commitLabel = (c: CareerCommit) => c.scope ?? c.tag ?? c.hash;

/** One value per log row so every row stays on one line. */
export const formatStart = (c: CareerCommit) => (c.status === 'upcoming' ? 'next' : c.start);

/** Full span, shown in the `git show` pane. */
export const formatSpan = (c: CareerCommit) => (c.end === null ? c.start : `${c.start} → ${c.end}`);

/* ------------------------------------------------------------------------------------------------
 * Drawing
 * ---------------------------------------------------------------------------------------------- */

const ROW_H = 32;
const MID = ROW_H / 2;
const GRAPH_W = 60;
const LANE_X: Record<CommitLane, number> = { main: 12, work: 30, lead: 48 };
/** Single-lane graph used in the stacked (mobile) layout. */
const MOBILE_X = 9;
const STROKE_W = 1.5;

const STROKE: Record<CommitLane, string> = {
  main: 'stroke-accent/60',
  work: 'stroke-sky-400/60',
  lead: 'stroke-fg-faint',
};

const NODE_STROKE: Record<CommitLane, string> = {
  main: 'stroke-accent',
  work: 'stroke-sky-400',
  lead: 'stroke-fg-muted',
};

const MOBILE_NODE: Record<CommitLane, string> = {
  main: 'h-2 w-2 border-accent',
  work: 'h-2 w-2 border-sky-400',
  lead: 'h-1.5 w-1.5 border-fg-muted',
};

const MAIN_RAIL = 'border-l-[1.5px] border-accent/60';

const segmentPath = ({ lane, kind }: Segment) => {
  const x = LANE_X[lane];
  const x0 = LANE_X.main;
  switch (kind) {
    case 'up':
      return `M${x} 0 V${MID}`;
    case 'down':
      return `M${x} ${MID} V${ROW_H}`;
    case 'fork':
      return `M${x0} ${MID} C${x0} ${MID * 0.4} ${x} ${MID * 0.6} ${x} 0`;
    case 'merge':
      return `M${x} ${MID} C${x} ${MID * 0.6} ${x0} ${MID * 0.4} ${x0} 0`;
  }
};

/** ● HEAD, ◌ upcoming, ◇ tagged milestone, ○ everything else. */
const GraphNode: React.FC<{ commit: CareerCommit; isHead: boolean }> = ({ commit, isHead }) => {
  const cx = LANE_X[commit.lane];
  if (isHead) {
    return (
      <>
        <circle cx={cx} cy={MID} r={4.5} className="head-pulse fill-sky-400/50" />
        <circle cx={cx} cy={MID} r={8} fill="none" strokeWidth={1} className="stroke-sky-400/35" />
        <circle
          cx={cx}
          cy={MID}
          r={4.5}
          strokeWidth={STROKE_W}
          className="fill-sky-400 stroke-sky-400 drop-shadow-[0_0_6px_rgb(56_189_248/0.8)]"
        />
      </>
    );
  }
  if (commit.status === 'upcoming') {
    return (
      <circle cx={cx} cy={MID} r={4} strokeWidth={STROKE_W} strokeDasharray="2 2" className="fill-ink-900 stroke-accent/70" />
    );
  }
  if (commit.tag) {
    const r = 4.75;
    return (
      <path
        d={`M${cx} ${MID - r} L${cx + r} ${MID} L${cx} ${MID + r} L${cx - r} ${MID} Z`}
        strokeWidth={STROKE_W}
        strokeLinejoin="round"
        className={`fill-ink-900 ${NODE_STROKE[commit.lane]}`}
      />
    );
  }
  return (
    <circle
      cx={cx}
      cy={MID}
      r={commit.lane === 'lead' ? 3 : 4}
      strokeWidth={STROKE_W}
      className={`fill-ink-900 ${NODE_STROKE[commit.lane]}`}
    />
  );
};

/** Split layout: every lane, with fork/merge curves. */
const LaneCell: React.FC<{ commit: CareerCommit; row: GraphRow }> = ({ commit, row }) => (
  <svg aria-hidden width={GRAPH_W} height={ROW_H} className="shrink-0 overflow-visible">
    {row.segments.map((s, k) => (
      <path
        key={k}
        d={segmentPath(s)}
        fill="none"
        strokeWidth={STROKE_W}
        strokeDasharray={s.dashed ? '3 2' : undefined}
        className={STROKE[s.lane]}
      />
    ))}
    <GraphNode commit={commit} isHead={row.isHead} />
  </svg>
);

/** Stacked layout: one lane, nodes only. */
const MobileLaneCell: React.FC<{ commit: CareerCommit; index: number; count: number; present: number }> = ({
  commit,
  index,
  count,
  present,
}) => {
  const shape =
    commit.status === 'upcoming'
      ? 'h-2 w-2 rounded-full border-dashed border-accent/70'
      : commit.tag
        ? `h-2 w-2 rotate-45 rounded-[1px] ${MOBILE_NODE[commit.lane]}`
        : `rounded-full ${MOBILE_NODE[commit.lane]}`;
  const head = commit.status === 'current' && commit.lane === 'work';
  return (
    <span aria-hidden className="relative w-5 shrink-0 self-stretch">
      {index > 0 && (
        <span
          className={`absolute top-0 ${MAIN_RAIL} ${index <= present ? 'border-dashed' : ''}`}
          style={{ left: MOBILE_X - STROKE_W / 2, height: MID }}
        />
      )}
      {index < count - 1 && (
        <span
          className={`absolute bottom-0 ${MAIN_RAIL} ${index < present ? 'border-dashed' : ''}`}
          style={{ left: MOBILE_X - STROKE_W / 2, top: MID }}
        />
      )}
      <span
        className={`absolute -translate-x-1/2 -translate-y-1/2 border-[1.5px] bg-ink-900 ${shape} ${
          head ? '!bg-sky-400 shadow-[0_0_8px_rgb(56_189_248/0.8)]' : ''
        }`}
        style={{ left: MOBILE_X, top: MID }}
      />
    </span>
  );
};

/* ------------------------------------------------------------------------------------------------
 * Log row + git show
 * ---------------------------------------------------------------------------------------------- */

const PILL: Record<Ref['kind'], Partial<Record<CommitLane, string>> & { default: string }> = {
  head: { default: 'border-sky-400/50 bg-sky-400/15 text-sky-300' },
  branch: { default: 'border-fg-faint text-fg-muted', work: 'border-sky-400/45 text-sky-300' },
  upcoming: { default: 'border-dashed border-accent/60 text-accent' },
};

const Decorations: React.FC<{ refs: Ref[]; tag?: string }> = ({ refs, tag }) => {
  if (!refs.length && !tag) return null;
  return (
    <>
      {refs.map((r) => (
        <span
          key={r.label}
          className={`whitespace-nowrap rounded border px-1.5 text-[10px] leading-[18px] ${PILL[r.kind][r.lane] ?? PILL[r.kind].default}`}
        >
          {r.label}
        </span>
      ))}
      {tag && (
        <span className="whitespace-nowrap text-[11px]">
          <span className="text-fg-faint">(</span>
          <span className="text-yellow-300/90">tag: {tag}</span>
          <span className="text-fg-faint">)</span>
        </span>
      )}
    </>
  );
};

/** One line: hash, short name, date, and a marker on the selected row. */
const RowText: React.FC<{ commit: CareerCommit; selected: boolean; split: boolean }> = ({ commit, selected, split }) => {
  const upcoming = commit.status === 'upcoming';
  const label = selected
    ? 'text-fg'
    : upcoming
      ? 'text-fg/60 group-hover:text-fg/80'
      : commit.lane === 'lead'
        ? 'text-fg-muted group-hover:text-fg/90'
        : 'text-fg/85 group-hover:text-fg';
  const Marker = split ? ChevronLeft : ChevronDown;
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2.5 pr-1.5 font-mono text-[12px] leading-none">
      <span className={`w-[7ch] shrink-0 text-fg-faint ${split ? '' : 'hidden sm:inline'}`}>{commit.hash}</span>
      <span className={`min-w-0 flex-1 truncate transition-colors ${label}`}>{commitLabel(commit)}</span>
      <span className={`shrink-0 text-[11px] tabular-nums ${upcoming ? 'text-accent' : 'text-fg-faint'}`}>
        {formatStart(commit)}
      </span>
      <Marker
        aria-hidden
        size={13}
        strokeWidth={2.25}
        className={`shrink-0 text-accent transition-opacity ${selected ? 'opacity-100' : 'opacity-0'}`}
      />
    </span>
  );
};

const Subject: React.FC<{ commit: CareerCommit }> = ({ commit }) => (
  <>
    {commit.type && (
      <span className="font-normal text-fg-faint">
        {commit.type}
        {commit.scope && (
          <>
            (<span className="text-accent">{commit.scope}</span>)
          </>
        )}
        :{' '}
      </span>
    )}
    <span className={commit.status === 'upcoming' ? 'text-fg/75' : 'text-fg'}>{commit.message}</span>
  </>
);

const CommitShow: React.FC<{ commit: CareerCommit; row: GraphRow }> = ({ commit, row }) => (
  <div className="font-mono text-[12px] leading-relaxed">
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-fg-faint">
      <span>
        commit <span className="text-accent">{commit.hash}</span>
      </span>
      <Decorations refs={row.refs} tag={commit.tag} />
    </p>
    <h3 className="mt-3 text-[15px] font-medium leading-snug tracking-tight">
      <Subject commit={commit} />
    </h3>
    <p className="mt-2 break-words text-fg-muted">
      {commit.role} · {commit.org}
    </p>
    <p className="text-fg-faint">{formatSpan(commit)}</p>
    {commit.details.length > 0 && (
      <ul className="mt-4 space-y-1.5 text-emerald-400">
        {commit.details.map((line) => (
          <li key={line} className="break-words pl-[2ch] [text-indent:-2ch]">
            + {line}
          </li>
        ))}
      </ul>
    )}
  </div>
);

/* ------------------------------------------------------------------------------------------------
 * Hooks
 * ---------------------------------------------------------------------------------------------- */

/** Flips to true the first time `ref` scrolls into view. */
function useInView(ref: React.RefObject<Element | null>) {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return inView;
}

/** True while `ref` overlaps the middle band of the viewport, i.e. while it's what you're reading. */
function useInFocusBand(ref: React.RefObject<Element | null>) {
  const [on, setOn] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !('IntersectionObserver' in window)) {
      setOn(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setOn(entry.isIntersecting), {
      rootMargin: '-35% 0px -35% 0px',
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return on;
}

/** Live `matchMedia`; `fallback` where it isn't available (jsdom). */
function useMediaQuery(query: string, fallback: boolean) {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : fallback
  );

  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia(query);
    const update = () => setMatches(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, [query]);

  return matches;
}

/** Types `text` out once, `startMs` after `enabled` turns true; shows it whole when motion is reduced. */
function useTyped(text: string, startMs: number, enabled: boolean) {
  const reduced = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [count, setCount] = useState(reduced ? text.length : 0);

  useEffect(() => {
    if (reduced || !enabled) return;
    let interval: number | undefined;
    const start = window.setTimeout(() => {
      interval = window.setInterval(() => {
        setCount((c) => {
          if (c + 1 >= text.length) window.clearInterval(interval);
          return Math.min(c + 1, text.length);
        });
      }, 55);
    }, startMs);
    return () => {
      window.clearTimeout(start);
      window.clearInterval(interval);
    };
  }, [text, startMs, reduced, enabled]);

  return text.slice(0, count);
}

/* ------------------------------------------------------------------------------------------------
 * Card
 * ---------------------------------------------------------------------------------------------- */

const MODEL = buildGraph(careerLog);
const LAST = careerLog.length - 1;
const HEAD = MODEL.rows.findIndex((r) => r.isHead);
const PRESENT = HEAD >= 0 ? HEAD : careerLog.findIndex((c) => c.status !== 'upcoming');

/** Width of the log pane in the split layout; the header uses the same columns. */
const SPLIT_COLS = 'grid-cols-[minmax(0,23rem)_minmax(0,1fr)]';
/** How long the pointer has to rest on a row before it takes over the `git show` pane. */
const HOVER_MS = 60;
const COMMAND = 'git switch caterpillar';

const rowDelay = (i: number) => ({ animationDelay: `${300 + i * 90}ms` });

const isEditable = (el: EventTarget | null) =>
  el instanceof HTMLElement && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

/** Selection target for a key press, or null if the key isn't a list key. */
function keyTarget(e: { key: string; altKey: boolean; ctrlKey: boolean; metaKey: boolean }, from: number) {
  if (e.altKey || e.ctrlKey || e.metaKey) return null;
  switch (e.key) {
    case 'j':
    case 'ArrowDown':
      return Math.min(from + 1, LAST);
    case 'k':
    case 'ArrowUp':
      return Math.max(from - 1, 0);
    case 'Home':
      return 0;
    case 'End':
      return LAST;
    default:
      return null;
  }
}

const CareerGraph: React.FC = () => {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref);
  const reading = useInFocusBand(ref);
  const split = useMediaQuery('(min-width: 768px)', true);
  const typed = useTyped(COMMAND, 300 + careerLog.length * 90 + 400, inView);
  const id = useId();

  const [selected, setSelected] = useState(HEAD >= 0 ? HEAD : 0);
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const hoverTimer = useRef<number | undefined>(undefined);
  const commit = careerLog[selected];

  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);

  // j/k work without focusing the card first, while it's the thing on screen.
  useEffect(() => {
    if (!reading) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || isEditable(e.target) || (e.key !== 'j' && e.key !== 'k')) return;
      setSelected((s) => keyTarget(e, s) ?? s);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reading]);

  // With focus in the list, the list keys also move focus along with the selection.
  const onListKeyDown = (e: React.KeyboardEvent) => {
    const next = keyTarget(e, selected);
    if (next === null) return;
    e.preventDefault();
    setSelected(next);
    rowRefs.current[next]?.focus();
  };

  const hoverSelect = (i: number) => {
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setSelected(i), HOVER_MS);
  };
  const cancelHover = () => window.clearTimeout(hoverTimer.current);

  const rowId = (i: number) => `${id}-row-${i}`;
  const panelId = (i: number) => `${id}-show-${i}`;
  const rowClass = (i: number) =>
    `git-fade group flex w-full items-center rounded-md text-left transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent/40 ${
      i === selected ? 'bg-ink-800/70' : 'hover:bg-ink-800/35'
    }`;

  const branchBadge = MODEL.headBranch && (
    <span className="flex shrink-0 items-center gap-1.5 text-fg-muted">
      <GitBranch size={12} className="text-sky-400" />
      {MODEL.headBranch}
    </span>
  );

  return (
    <figure ref={ref} className={`graph-reveal relative min-w-0 ${inView ? 'is-visible' : ''}`}>
      <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-accent/25 via-ink-700/40 to-transparent" aria-hidden />
      <div className="relative overflow-hidden rounded-2xl bg-ink-900">
        <div className={`grid border-b border-ink-700 font-mono text-[11px] text-fg-faint ${split ? SPLIT_COLS : ''}`}>
          <div className={`flex items-center justify-between gap-4 px-4 py-3 ${split ? 'border-r border-ink-700' : ''}`}>
            <span className="min-w-0 truncate">
              ~/abhinav <span className="text-fg-muted">· git log --graph --all</span>
            </span>
            {!split && branchBadge}
          </div>
          {split && (
            <div className="flex min-w-0 items-center justify-between gap-4 px-6 py-3">
              <span className="min-w-0 truncate">
                <span className="text-fg-muted">git show</span> <span className="text-accent">{commit.hash}</span>
              </span>
              {branchBadge}
            </div>
          )}
        </div>

        {split ? (
          <div className={`grid ${SPLIT_COLS}`}>
            <div
              role="tablist"
              aria-orientation="vertical"
              aria-label="Career log"
              onKeyDown={onListKeyDown}
              onMouseLeave={cancelHover}
              className="border-r border-ink-700 px-2 py-3"
            >
              {careerLog.map((c, i) => (
                <button
                  key={c.hash + i}
                  ref={(el) => {
                    rowRefs.current[i] = el;
                  }}
                  type="button"
                  role="tab"
                  id={rowId(i)}
                  aria-selected={i === selected}
                  aria-controls={panelId(i)}
                  tabIndex={i === selected ? 0 : -1}
                  onClick={() => {
                    cancelHover();
                    setSelected(i);
                  }}
                  onMouseEnter={() => hoverSelect(i)}
                  onMouseLeave={cancelHover}
                  className={rowClass(i)}
                  style={{ ...rowDelay(i), height: ROW_H }}
                >
                  <LaneCell commit={c} row={MODEL.rows[i]} />
                  <RowText commit={c} selected={i === selected} split />
                </button>
              ))}
            </div>

            {/* Every panel sits in the same grid cell, so the pane is always as tall as the longest one
                and switching never makes the page jump. */}
            <div className="git-fade grid min-w-0 px-6 py-5" style={rowDelay(1)}>
              {careerLog.map((c, i) => (
                <div
                  key={c.hash + i}
                  role="tabpanel"
                  id={panelId(i)}
                  aria-labelledby={rowId(i)}
                  tabIndex={i === selected ? 0 : -1}
                  inert={i !== selected}
                  className={`col-start-1 row-start-1 min-w-0 rounded-md transition-[opacity,visibility] duration-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent/40 motion-reduce:transition-none ${
                    i === selected ? 'visible opacity-100' : 'invisible opacity-0'
                  }`}
                >
                  <CommitShow commit={c} row={MODEL.rows[i]} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <ol className="px-2 py-3" onKeyDown={onListKeyDown}>
            {careerLog.map((c, i) => {
              const open = i === selected;
              return (
                <li key={c.hash + i}>
                  <button
                    ref={(el) => {
                      rowRefs.current[i] = el;
                    }}
                    type="button"
                    id={rowId(i)}
                    aria-expanded={open}
                    aria-controls={panelId(i)}
                    onClick={() => setSelected(i)}
                    className={rowClass(i)}
                    style={{ ...rowDelay(i), height: ROW_H }}
                  >
                    <MobileLaneCell commit={c} index={i} count={careerLog.length} present={PRESENT} />
                    <RowText commit={c} selected={open} split={false} />
                  </button>
                  <div
                    id={panelId(i)}
                    role="region"
                    aria-labelledby={rowId(i)}
                    inert={!open}
                    className={`grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none ${
                      open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
                  >
                    <div className="min-h-0 overflow-hidden">
                      <div className="relative flex">
                        <span aria-hidden className="relative w-5 shrink-0">
                          {i < LAST && (
                            <span
                              className={`absolute inset-y-0 ${MAIN_RAIL} ${i < PRESENT ? 'border-dashed' : ''}`}
                              style={{ left: MOBILE_X - STROKE_W / 2 }}
                            />
                          )}
                        </span>
                        <div className="min-w-0 flex-1 pb-3 pt-1">
                          <div className="rounded-lg border border-ink-700 bg-ink/70 px-3.5 py-3">
                            <CommitShow commit={c} row={MODEL.rows[i]} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        <div className="flex items-center gap-2 border-t border-ink-700 px-4 py-3 font-mono text-[11px] text-fg-muted">
          <span className="text-accent">$</span>
          <span className="truncate">{typed}</span>
          <span className="h-3.5 w-1.5 shrink-0 animate-blink bg-accent/80" aria-hidden />
          {split && (
            <span aria-hidden className="ml-auto hidden shrink-0 text-fg-faint lg:inline">
              j/k · ↑↓ to move
            </span>
          )}
        </div>
      </div>
    </figure>
  );
};

export default CareerGraph;
